import * as z from 'zod';

import { isDefined, isEmpty, isNotDefined } from '@hsp/utilities';

import { s } from '@u/strings';

import { ClientSchema, IClient } from 'types/api';

const BaseSchema = z.object({
  cardAmount: z.number().gte(0, 'Amount must be 0 or greater').default(0),
  cardDonors: z.number().gte(0, 'Amount must be 0 or greater').default(0),
  cashAmount: z.number().gte(0, 'Amount must be 0 or greater').default(0),
  cashDonors: z.number().gte(0, 'Amount must be 0 or greater').default(0),
  checkAmount: z.number().gte(0, 'Amount must be 0 or greater').default(0),
  checkDonors: z.number().gte(0, 'Amount must be 0 or greater').default(0),
  client: z.preprocess(
    (arg, context) => {
      if (isNotDefined(arg) || isEmpty(arg)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: s(['Site.Pages.AudienceBuilder.Save.Validation.Client.Required']),
        });

        return z.NEVER;
      }

      const { id, name } = arg as IClient;

      if (isNotDefined(id) || isNotDefined(name)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: s(['Site.Pages.AudienceBuilder.Save.Validation.Client.Required']),
        });

        return z.NEVER;
      }

      return arg;
    },
    ClientSchema.superRefine((client, context) => {
      const { id, name } = client;

      if (isNotDefined(id) || isNotDefined(name)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: s(['Site.Pages.AudienceBuilder.Save.Validation.Client.Required']),
        });

        return z.NEVER;
      }

      return client;
    })
  ),
  dateCaged: z
    .date({ invalid_type_error: 'Field Required' })
    .default(new Date())
    .refine((arg) => isEmpty(arg) || isNotDefined(arg), 'Field Required'),
  mailCode: z.string().default('WM'),
  nonDonors: z.number().gte(0, 'Amount must be 0 or greater').default(0),
});

export const EditSchema = BaseSchema.omit({ mailCode: true }).extend({
  donors: z.number().optional(),
  enteredBy: z.string().nullish().optional(),
  enteredDate: z.date().optional(),
  gross: z.number().optional(),
  source: z.string().optional(),
  type: z.literal('edit'),
});
export const WhiteMailSchema = BaseSchema.extend({
  client: ClientSchema.optional(),
  dateCaged: z.date().default(new Date()).optional(),
  file: z.any().optional(),
  hidePreview: z.boolean().default(true),
  type: z.literal('add-whitemail'),
});

export const CagingEntrySchema = WhiteMailSchema.extend({
  mailCode: z.string().optional(),
  type: z.literal('add-caging'),
});

export const DailiesSchema = z
  .discriminatedUnion('type', [CagingEntrySchema, WhiteMailSchema, EditSchema])
  .superRefine((arg, context) => {
    const { type } = arg;

    if (type === 'add-caging' || type === 'add-whitemail') {
      const { hidePreview, file, client, dateCaged, mailCode } = arg;

      if (hidePreview) {
        if (isEmpty(file) || ['text/csv'].includes(file?.[0]?.type) === false) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            fatal: true,
            message: 'only csv',
            path: ['file'],
          });

          return z.NEVER;
        }
      }

      if (!hidePreview) {
        if (isNotDefined(client)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            fatal: true,
            message: s(['Site.Pages.AudienceBuilder.Save.Validation.Client.Required']),
            path: ['client'],
          });
        }

        if (isNotDefined(dateCaged)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            fatal: true,
            message: 'Field Required',
            path: ['dateCaged'],
          });
        }
        if (isNotDefined(mailCode) || isEmpty(mailCode)) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            fatal: true,
            message: 'Field Required',
            path: ['mailCode'],
          });
        }
        if (isDefined(client)) {
          const { id, name } = client as IClient;

          if (isNotDefined(id) || isNotDefined(name)) {
            context.addIssue({
              code: z.ZodIssueCode.custom,
              fatal: true,
              message: s(['Site.Pages.AudienceBuilder.Save.Validation.Client.Required']),
              path: ['client'],
            });

            return z.NEVER;
          }
        }

        return z.NEVER;
      }
    }
  });

export type DailiesForm = z.infer<typeof DailiesSchema>;
